import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'src/utils/axios';
import { useWebSocket } from 'src/hooks/useWebSocket';
import type {
  CaseDetail,
  CaseEvent,
  CaseSocketFrame,
  CaseSummary,
  SupportAttachmentRef,
  SupportDocument,
} from 'src/components/SupportCase/types';

interface UseCaseStreamOptions {
  caseId: number | null;
  enabled?: boolean;
}

interface UseCaseStreamResult {
  detail: CaseDetail | null;
  events: CaseEvent[];
  /** Text the model is still generating, replaced by the persisted event. */
  streaming: string;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  send: (
    message: string,
    clientMsgId: string,
    attachmentIds?: number[]
  ) => Promise<boolean>;
  approve: (
    approvalId: string,
    decision: 'confirm' | 'decline'
  ) => Promise<boolean>;
  refetch: () => Promise<void>;
}

/**
 * The case stream (ENG-4137).
 *
 * Two channels, one source of truth. The socket delivers events as they happen; a
 * cursor refetch (`GET /events/?after=`) covers every gap — first load, a reconnect,
 * a socket that silently died. The refetch is cheap because the cursor is a keyset
 * on the event id, so it never replays the conversation.
 *
 * There is no SSE client here on purpose: Thor has no streaming HTTP view, and the
 * panel must survive a reload, which a socket plus a cursor does and a long-lived
 * response does not.
 */
export const useCaseStream = (
  options: UseCaseStreamOptions
): UseCaseStreamResult => {
  const { caseId, enabled = true } = options;
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const [events, setEvents] = useState<CaseEvent[]>([]);
  const [streaming, setStreaming] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cursorRef = useRef(0);
  // The reconciler reads the state the panel last saw, not a stale closure value.
  const stateRef = useRef<string>('agent_active');
  const reconcileRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const mergeEvents = useCallback((incoming: CaseEvent[]) => {
    if (incoming.length === 0) {
      return;
    }
    setEvents((current) => {
      const seen = new Set(current.map((event) => event.id));
      const merged = [...current];
      incoming.forEach((event) => {
        if (!seen.has(event.id)) {
          merged.push(event);
          seen.add(event.id);
        }
      });
      merged.sort((a, b) => a.id - b.id);
      cursorRef.current = merged.length > 0 ? merged[merged.length - 1].id : 0;
      return merged;
    });
  }, []);

  const refetch = useCallback(async () => {
    if (caseId === null) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.get(`/support/cases/${caseId}/`);
      const payload: CaseDetail = response.data;
      setDetail(payload);
      setEvents(payload.events);
      cursorRef.current = payload.cursor;
      stateRef.current = payload.case.state;
      setError(null);
    } catch {
      // No snackbar: the panel renders this inline next to the data it failed to load.
      setError('We could not load this case. Try again in a moment.');
    } finally {
      setIsLoading(false);
    }
  }, [caseId]);

  const catchUp = useCallback(async () => {
    if (caseId === null) {
      return;
    }
    try {
      const response = await axios.get(
        `/support/cases/${caseId}/events/?after=${cursorRef.current}`
      );
      mergeEvents(response.data.events as CaseEvent[]);
    } catch {
      setError('Live updates paused. Reload to catch up.');
    }
  }, [caseId, mergeEvents]);

  useEffect(() => {
    if (caseId === null || !enabled) {
      return undefined;
    }
    void refetch();
    return undefined;
  }, [caseId, enabled, refetch]);

  const stopReconcile = useCallback(() => {
    if (reconcileRef.current !== null) {
      clearInterval(reconcileRef.current);
      reconcileRef.current = null;
    }
  }, []);

  useEffect(() => stopReconcile, [stopReconcile]);

  /**
   * Keep the panel honest after a write.
   *
   * The socket is the fast path, not the only one. A turn that finishes without its
   * frame arriving (a dropped connection, a worker that cannot reach the channel
   * layer) used to leave the panel saying "working" forever, with the composer
   * disabled — the worst possible failure, because the answer exists and the user
   * cannot reply to it. So after every write the cursor is reconciled on a short
   * interval until the case stops being busy. Bounded: it stops on the first
   * non-busy state, and never runs when nothing was sent.
   */
  const startReconcile = useCallback(() => {
    stopReconcile();
    let ticks = 0;
    reconcileRef.current = setInterval(async () => {
      ticks += 1;
      await catchUp();
      if (stateRef.current !== 'agent_active' || ticks > 40) {
        stopReconcile();
      }
    }, 1500);
  }, [catchUp, stopReconcile]);

  const handleFrame = useCallback(
    (frame: CaseSocketFrame) => {
      switch (frame.type) {
        case 'case_event': {
          setStreaming('');
          mergeEvents([frame.data]);
          const event = frame.data;
          if (event.event_type === 'agent_message') {
            stateRef.current = 'awaiting_user';
          }
          if (event.event_type === 'attachment') {
            // ENG-4272 — the detail is loaded once, so its `documents` array predates
            // anything the Assistant publishes during the conversation. Without this,
            // the panel's "Documents in this conversation" list stays empty (and the
            // chip for the new document resolves as removed) until a page reload.
            //
            // Merged from the event rather than re-fetched: the payload carries
            // everything the list renders, and a refetch here would replace the
            // timeline the deltas have been building.
            const rows = Array.isArray(event.payload?.attachments)
              ? (event.payload.attachments as SupportAttachmentRef[])
              : [];
            if (rows.length > 0) {
              setDetail((current) => {
                if (!current) {
                  return current;
                }
                const known = new Set(
                  (current.documents ?? []).map((document) => document.id)
                );
                const additions: SupportDocument[] = rows
                  .filter((row) => !known.has(row.id))
                  .map((row) => ({
                    id: row.id,
                    name: row.name,
                    content_type: row.content_type ?? '',
                    size: row.size ?? 0,
                    origin: row.origin ?? 'agent',
                    case: current.case.id,
                    case_title: '',
                    ready: true,
                    deleted: false,
                    // The event's own timestamp: the payload names no created_at, and
                    // the row was created for this event.
                    created_at: event.created_at ?? null,
                  }));
                if (additions.length === 0) {
                  return current;
                }
                return {
                  ...current,
                  documents: [...additions, ...(current.documents ?? [])],
                };
              });
            }
          }
          break;
        }
        case 'case_delta':
          setStreaming((current) => current + frame.data.delta);
          break;
        case 'case_state': {
          const summary = frame.data as CaseSummary;
          stateRef.current = summary.state;
          setDetail((current) =>
            current ? { ...current, case: summary } : current
          );
          break;
        }
        default:
          break;
      }
    },
    [mergeEvents]
  );

  const { isConnected } = useWebSocket({
    enabled: enabled && caseId !== null,
    url: `support-case/${caseId}/`,
    messageHandlers: {
      case_ready: () => {
        // A (re)connect may have missed frames while we were away.
        void catchUp();
      },
      case_event: (data) =>
        handleFrame({ type: 'case_event', data: data as CaseEvent }),
      case_delta: (data) =>
        handleFrame({
          type: 'case_delta',
          data: data as { delta: string },
        }),
      case_state: (data) =>
        handleFrame({ type: 'case_state', data: data as CaseSummary }),
    },
  });

  const send = useCallback(
    async (
      message: string,
      clientMsgId: string,
      attachmentIds: number[] = []
    ): Promise<boolean> => {
      if (caseId === null || !message.trim()) {
        return false;
      }
      try {
        await axios.post(`/support/cases/${caseId}/messages/`, {
          message,
          client_msg_id: clientMsgId,
          // The files travel with the message, not before it: the event timeline
          // names them ahead of the question and the turn's `state.attachments`
          // carries their ids (ENG-4185).
          attachment_ids: attachmentIds,
        });
        setError(null);
        const live = await axios.get(`/support/cases/${caseId}/`);
        stateRef.current = live.data?.case?.state || 'agent_active';
        mergeEvents((live.data?.events || []) as CaseEvent[]);
        startReconcile();
        return true;
      } catch {
        setError('That message did not send. Try again.');
        return false;
      }
    },
    [caseId, mergeEvents, startReconcile]
  );

  const approve = useCallback(
    async (
      approvalId: string,
      decision: 'confirm' | 'decline'
    ): Promise<boolean> => {
      if (caseId === null) {
        return false;
      }
      try {
        await axios.post(`/support/cases/${caseId}/approvals/${approvalId}/`, {
          decision,
        });
        setError(null);
        startReconcile();
        return true;
      } catch {
        setError('That approval could not be completed. Ask again.');
        return false;
      }
    },
    [caseId, startReconcile]
  );

  return {
    detail,
    events,
    streaming,
    isConnected,
    isLoading,
    error,
    send,
    approve,
    refetch,
  };
};
